import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Package } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requestPasswordReset } from '@/lib/auth-client';
import { BUILD_STAMP } from '@/lib/build-info';

/**
 * Ask for a reset link.
 *
 * The confirmation is the same whether or not the address belongs to an account.
 * That is deliberate and worth not "fixing": this page is reachable by anyone on
 * the internet, and a form that says "no such account" is a way to find out
 * which of a list of guessed addresses are real. Someone who mistypes their own
 * address gets no mail and tries again, which is the cost of not answering that
 * question for a stranger.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const { error: requestError } = await requestPasswordReset({
        email: email.trim(),
        redirectTo: '/reset-password',
      });

      // Only genuine faults surface — being throttled, or the server being
      // down. "No such account" is not one of them and never reaches here.
      if (requestError) {
        setError(
          requestError.status === 429
            ? 'Too many requests. Wait a minute and try again.'
            : 'Could not send the email just now. Try again, or ask an administrator.',
        );
        return;
      }

      setSent(true);
    } catch {
      setError('Could not reach the server. Check that the app is running.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ShipSwift</h1>
          <p className="text-sm text-slate-500">Gold Star Printing</p>
        </div>

        {sent ? (
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="font-semibold text-slate-900">Check your email</p>
                <p className="text-sm text-slate-600">
                  If <span className="font-medium">{email.trim()}</span> belongs to a ShipSwift
                  account, a link to choose a new password is on its way. It works once and
                  expires in an hour.
                </p>
                <p className="text-sm text-slate-500">
                  Nothing arrived? Check the spam folder, then ask an administrator — they can
                  set a password for you directly.
                </p>
              </div>
            </div>

            <Link to="/login" className="block">
              <Button variant="outline" className="w-full gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </Button>
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 space-y-4"
          >
            <div>
              <h2 className="font-semibold text-slate-900">Forgot your password?</h2>
              <p className="text-sm text-slate-600 mt-1">
                Enter the email address for your account and we&apos;ll send a link to choose a
                new password.
              </p>
            </div>

            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 h-11"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                'Send reset link'
              )}
            </Button>

            <Link
              to="/login"
              className="block text-sm text-center text-slate-500 hover:text-slate-800 pt-1"
            >
              Back to sign in
            </Link>
          </form>
        )}

        <p className="text-xs text-center text-slate-400 mt-6">{BUILD_STAMP}</p>
      </div>
    </div>
  );
}
